// @ts-strict-ignore
import uniqBy from 'lodash/uniqBy';

import {
  DARK_GRAY, GREEN, GRAY, TRANSPARENT_GREEN,
  SLIDER_MARKER_THRESHOLD,
} from './constants';
import type { ChartLevels } from './types';


export function getSliderPercentileFromBandValue({ bandValue, operator }) {
  switch (operator) {
    case 'lt':
      return bandValue + 1;
    case 'gt':
      return bandValue - 1;
  }
  return bandValue;
}

export function colorDataLineByThresholdOperatorAndValue({
  bandValue,
  breakdownMap,
  chartLevels,
  operator,
}: {
  bandValue: number;
  breakdownMap: Map<number, number>;
  chartLevels: ChartLevels;
  operator: string;
}) {
  const sliderPercentile = getSliderPercentileFromBandValue({ bandValue, operator });
  const value = breakdownMap.get(sliderPercentile);
  const level = chartLevels[value];
  if (level === undefined) {
    return [];
  }
  switch (operator) {
    case 'e':
      return [
        { value: chartLevels[value].minPercentile, color: GRAY },
        { value: chartLevels[value].maxPercentile, color: GREEN },
        { value: 99, color: GRAY },
      ];
    case 'ne':
      return [
        { value: chartLevels[value].minPercentile - 1, color: GREEN },
        { value: chartLevels[value].maxPercentile + 1, color: GRAY },
        { value: 99, color: GREEN },
      ];
    case 'lt':
      return [
        { value: level.minPercentile - 1, color: GREEN },
        { value: 99, color: GRAY },
      ];
    case 'le': {
      return [
        { value: level.maxPercentile, color: GREEN },
        { value: 99, color: GRAY },
      ];
    }
    case 'gt':
      return [
        { value: level.maxPercentile + 1, color: GRAY },
        { value: 99, color: GREEN },
      ];
    case 'ge': {
      return [
        { value: level.minPercentile, color: GRAY },
        { value: 99, color: GREEN },
      ];
    }
  }
  return [
    { color: GRAY },
  ];
}

export function colorPlotBandsByThresholdOperatorAndValue({
  bandValue,
  breakdownMap,
  chartLevels,
  operator,
}: {
  bandValue: number;
  breakdownMap: Map<number, number>;
  chartLevels: ChartLevels;
  operator: string;
}) {
  const sharedBandOpts = { color: TRANSPARENT_GREEN, zIndex: 3 };
  const sliderPercentile = getSliderPercentileFromBandValue({ bandValue, operator });
  const value = breakdownMap.get(sliderPercentile);
  const level = chartLevels[value];
  if (level === undefined) {
    return [];
  }
  switch (operator) {
    case 'e':
      return [{
        ...sharedBandOpts,
        from: chartLevels[value].minPercentile,
        to: chartLevels[value].maxPercentile,
      }];
    case 'ne':
      return [{
        ...sharedBandOpts,
        from: 0,
        to: chartLevels[value].minPercentile - 1,
      }, {
        ...sharedBandOpts,
        from: chartLevels[value].maxPercentile + 1,
        to: 99,
      }];
    case 'lt':
      return [{ ...sharedBandOpts, from: 0, to: level.minPercentile - 1 }];
    case 'le': {
      return [{ ...sharedBandOpts, from: 0, to: level.maxPercentile }];
    }
    case 'gt':
      return [{ ...sharedBandOpts, from: level.maxPercentile + 1, to: 99 }];
    case 'ge': {
      return [{ ...sharedBandOpts, from: level.minPercentile, to: 99 }];
    }
  }
  return [];
}

export function colorPlotLinesByThresholdOperatorAndValue({
  bandValue,
  breakdownMap,
  chartLevels,
  operator,
}: {
  bandValue: number;
  breakdownMap: Map<number, number>;
  chartLevels: ChartLevels;
  operator: string;
}) {
  const sharedLineOpts = {
    color: DARK_GRAY,
    width: 1,
    dashStyle: 'Dash',
    zIndex: 4,
    label: '',
  };
  const value = breakdownMap.get(bandValue);
  const level = chartLevels[value];
  if (['e', 'ne', 'ge', 'gt', 'le', 'lt'].includes(operator) && level !== undefined) {
    if (operator === 'e') {
      return [
        {
          ...sharedLineOpts,
          value: level.minPercentile,
        },
        {
          ...sharedLineOpts,
          value: level.maxPercentile,
        },
      ];
    }
    if (operator === 'ne') {
      return [
        {
          ...sharedLineOpts,
          value: level.minPercentile - 1,
        },
        {
          ...sharedLineOpts,
          value: level.maxPercentile + 1,
        },
      ];
    }
    if (operator === 'ge' || operator === 'gt') {
      return [
        {
          ...sharedLineOpts,
          value: level.minPercentile,
        },
      ];
    }
    if (operator === 'le' || operator === 'lt') {
      return [
        {
          ...sharedLineOpts,
          value: level.maxPercentile,
        },
      ];
    }
  }
  return [{ ...sharedLineOpts, value }];
}

/*
 * Takes in the chart levels generated from distillChartLevelsFromBreakdownData, the current slider
 * percentile value, and the threshold operator and uses them to construct a set of slider marks
 * that will be fed into the Slider CL component. These slider marks correspond to the "levels"
 * within the breakdown data that appear and allow the user to easily select the proper value
 * when there are a few amount of values to select from.
 *
 * When there are more than 10 levels (this SLIDER_MARKER_THRESHOLD number is somewhat arbitrary for now),
 * we default to displaying the max, min, and current slider value instead of all marks for the levels.
 */
export function convertChartLevelsToSliderMarks(
  chartLevels: ChartLevels,
  sliderPercentile: number,
  thresholdOperator: string,
): Record<number, string> {
  if (Object.keys(chartLevels).length > SLIDER_MARKER_THRESHOLD) {
    return { 1: '1%', [sliderPercentile]: `${sliderPercentile}%`, 99: '99%' };
  }
  return Object.values(chartLevels).reduce((acc, level) => {
    let percentile = level.maxPercentile;
    let levelString = `${level.maxPercentile}%`;
    switch (thresholdOperator) {
      case 'e': {
        percentile = level.minPercentile;
        levelString = `${level.totalPercent}%`;
        break;
      }
      case 'ne': {
        percentile = level.minPercentile;
        levelString = `${99 - level.totalPercent}%`;
        break;
      }
      case 'le': {
        percentile = level.maxPercentile;
        levelString = `${level.maxPercentile}%`;
        break;
      }
      case 'lt': {
        percentile = level.minPercentile;
        levelString = `${level.minPercentile}%`;
        break;
      }
      case 'ge': {
        percentile = 99 - level.minPercentile;
        levelString = `${99 - level.minPercentile}%`;
        break;
      }
      case 'gt': {
        percentile = 99 - level.maxPercentile;
        levelString = `${99 - level.maxPercentile}%`;
        break;
      }
    }
    return { ...acc, [percentile]: levelString };
  }, {});
}

/*
 * Takes in breakdown data and turns it into an object where the key is a value represented
 * in the chart and the value is an object containing that value's minimum percentile, maximum
 * percentile and total percent of the chart.
 */
export function distillChartLevelsFromBreakdownData(
  breakdownMap: Map<number, number>,
  thresholdOperator: string,
  targetThreshold: number,
): ChartLevels {
  const breakdownData = Array.from(breakdownMap, (value => value)) || [];
  const uniqueValues = uniqBy(breakdownData, 1).map(([, value]) => value);
  const chartLevels = uniqueValues.reduce((acc, uniqValue) => {
    const relevantPercentiles = breakdownData
      .filter(([, value]) => uniqValue === value)
      .map(([percentile]) => percentile);
    const minPercentile = Math.min(...relevantPercentiles);
    const maxPercentile = Math.max(...relevantPercentiles);
    return {
      ...acc,
      [uniqValue]: {
        minPercentile,
        maxPercentile,
        totalPercent: maxPercentile - minPercentile,
      },
    };
  }, {});
  const levelValues = Object.keys(chartLevels);
  switch (thresholdOperator) {
    case 'le':
    case 'lt':
      chartLevels[targetThreshold] = levelValues.every(value => Number(value) < targetThreshold) ? {
        minPercentile: 99,
        maxPercentile: 99,
        totalPercent: 100,
      } : {
        minPercentile: 1,
        maxPercentile: 1,
        totalPercent: 0,
      };
      break;
    case 'ge':
    case 'gt':
      chartLevels[targetThreshold] = levelValues.every(value => Number(value) > targetThreshold) ? {
        minPercentile: 99,
        maxPercentile: 99,
        totalPercent: 100,
      } : {
        minPercentile: 1,
        maxPercentile: 1,
        totalPercent: 0,
      };
      break;
  }
  return chartLevels;
}

/*
 * Calculates the current slider percentile from the band value based on the threshold operator.
 */
export function getSliderPercentileByThresholdOperator({
  bandValue,
  breakdownMap,
  chartLevels,
  operator,
}: {
  bandValue: number;
  breakdownMap: Map<number, number>;
  chartLevels: ChartLevels;
  operator: string;
}) {
  if (!breakdownMap.size || !bandValue || bandValue === 99) {
    return 0;
  }
  const sliderPercentile = getSliderPercentileFromBandValue({ bandValue, operator });
  const value = breakdownMap.get(sliderPercentile);
  const level = chartLevels[value];
  if (level === undefined) {
    return 0;
  }
  switch (operator) {
    case 'le':
      return level.maxPercentile;
    case 'lt':
      return level.minPercentile - 1;
    case 'e':
    case 'ne':
    case 'ge':
      return level.minPercentile;
    case 'gt':
      return level.maxPercentile + 1;
  }
  return level.minPercentile;
}
